import { InvitationsApi } from "@/api/invitations/InvitationsApi";
import { queryKeys } from "@/api/queryKeys";
import type { OrgMembershipRole } from "@/auth/membershipRoles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useAmISuperadmin(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.profileFlags.isSuperadmin,
    queryFn: () => InvitationsApi.amISuperadmin(),
    enabled,
    staleTime: 60_000,
  });
}

export function useOrganizationInvitations(organizationId: string | undefined) {
  return useQuery({
    queryKey: organizationId ? queryKeys.invitations.forOrg(organizationId) : queryKeys.invitations.none,
    queryFn: () => {
      if (!organizationId) throw new Error("Keine Organisation ausgewählt.");
      return InvitationsApi.listForOrganization(organizationId);
    },
    enabled: Boolean(organizationId),
  });
}

function useInvalidate(organizationId: string | undefined) {
  const queryClient = useQueryClient();
  return () => {
    if (!organizationId) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.invitations.forOrg(organizationId) });
  };
}

export function useInviteMember(organizationId: string | undefined) {
  const invalidate = useInvalidate(organizationId);
  return useMutation({
    mutationFn: (input: { email: string; displayName: string; roles: OrgMembershipRole[] }) => {
      if (!organizationId) throw new Error("Keine Organisation ausgewählt.");
      return InvitationsApi.invite({ organizationId, ...input });
    },
    onSuccess: invalidate,
  });
}

export function useResendInvite(organizationId: string | undefined) {
  const invalidate = useInvalidate(organizationId);
  return useMutation({
    mutationFn: (invitationId: string) => InvitationsApi.resend(invitationId),
    onSuccess: invalidate,
  });
}

export function useRevokeInvite(organizationId: string | undefined) {
  const invalidate = useInvalidate(organizationId);
  return useMutation({
    mutationFn: (invitationId: string) => InvitationsApi.revoke(invitationId),
    onSuccess: invalidate,
  });
}
